import React from "react";
import "./styles/uiIntegratedImage.css";
import "./styles/staffSidebarButtons.css";
import { API_URL } from "./config";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { APP_BRAND_NAME } from "./constants/appBrand";

// Layouts
import PublicLayout from "./layouts/PublicLayout";
import PrivateLayoutAdmin from "./layouts/PrivateLayoutAdmin";
import StudentLayout from "./layouts/StudentLayout";
import { StudentProvider } from "./context/StudentContext";

// Seguridad
import PrivateRoute from "./components/PrivateRoute";
import RoleRoute from "./components/RoleRoute";
import MessagingGuard from "./components/MessagingGuard";

// Público
import PaginaPublicitaria from "./pages/Publica/PaginaPublicitaria";
import CoursesPage from "./pages/CoursesPage";
import PaginaCursoPublico from "./pages/Publica/PaginaCursoPublico";
import BasePresentacionCursos from "./components/BasePresentacionCursos";
import Pago from "./pages/Publica/Pago"; // (tu carrito antiguo, si lo sigues usando)"
import PagoRedirect from "./pages/Publica/PagoRedirect";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import Login from "./pages/Login/Login";
import NewRegistro from "./pages/Publica/NewRegistro";
import Terminos from "./pages/Publica/Terminos";
import Privacidad from "./pages/Publica/Privacidad";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import CheckEmail from "./pages/Publica/CheckEmail";
import VerifyEmail from "./pages/Publica/VerifyEmail";
import EmailBienvenida from "./pages/Configuracion/EmailBienvenida";
import SetupAccount from "./pages/SetupAccount";
import InvitePaymentLanding from "./pages/Publica/InvitePaymentLanding";


// Usuario
import Campus from "./pages/Campus";
import Resumenes from "./pages/Resumenes";
import TestTemas from "./pages/TestTemas";
import TestExamen from "./pages/TestExamen";
import Rendimiento from "./pages/Rendimiento";
import Informacion from "./pages/Informacion";
import CursoUsuarioRedirect from "./pages/CursoUsuarioRedirect";
import StudentTabRedirect from "./pages/StudentTabRedirect";
import MensajeriaInterna from "./pages/Enlaces_Administracion/Acciones_mencor/MensajeriaInterna";
import StudentComunicados from "./pages/StudentComunicados";

// Admin / Gestión
import GestionDirector from "./pages/GestionDirector";
import GestionInstructor from "./pages/GestionInstructor";
import ProgresoAlumnos from "./pages/Enlaces_Administracion/ProgresoAlumnos";
import Autorizaciones from "./pages/Enlaces_Administracion/Autorizaciones";

import CentroControlDirector from "./pages/Enlaces_Administracion/CentroControlDirector";
import GestionCursos from "./pages/Enlaces_Administracion/GestionCursos";
import Registro from "./pages/Enlaces_Administracion/Registro";
import Perfil from "./pages/Perfil";
import ActivityLogQuery from "./pages/Enlaces_Administracion/ActivityLogQuery";
import EditarCurso from "./pages/Enlaces_Administracion/Acciones_gc/Cursos_Creacion/EditarCurso";
import ConfiguracionTests from "./pages/Enlaces_Administracion/Acciones_gc/Cursos_Creacion/ConfiguracionTests";


import Registrousuario from "./pages/Enlaces_Administracion/Acciones_cdc/Registrousuario";
import GestorUsuarios from "./pages/Enlaces_Administracion/Acciones_cdc/GestorUsuarios";
import CrearCurso from "./pages/Enlaces_Administracion/Acciones_gc/Cursos_Creacion/CrearCurso";
import VistaCursosAdmin from "./pages/Enlaces_Administracion/Acciones_gc/Cursos_Creacion/VistaCursosAdmin";
import CursoDetalle from "./pages/Enlaces_cursos/CursoDetalle";
import Configuracion from "./pages/Configuracion/Configuracion";
import GestionAnuncios from "./pages/Enlaces_Administracion/GestionAnuncios";


//Not found page
import NotFoundPage from "./pages/NotFoundPage";
import RecursoNoDisponible from "./pages/RecursoNoDisponible";
import { clearSessionKeys, purgeLegacyAuthToken } from "./utils/sessionKeys";
import { isPublicPath, getCleanPath } from "./utils/publicPaths";
import { getRequestUrl, isApiRequest } from "./utils/apiRequestUrl";
import { MensajesProvider } from "./context/MensajesContext";
import SessionInactivityWatcher from "./components/SessionInactivityWatcher";
import BrowserSessionGate from "./components/BrowserSessionGate";
import WhatsAppFloatButton from "./components/WhatsAppFloatButton";
import {
  PLATFORM_DIRECTOR_ROLES,
  STAFF_ROLES,
  ROLES_CURSO_EDICION,
  ROLES_CREAR_CURSO,
  ROLES_REGISTRO_ALUMNOS,
  ROLES_GESTION_ANUNCIOS,
  ROLES_PROGRESO_ALUMNOS,
  ALUMNO_ROLES,
} from "./utils/roles";

/**
 * For same-origin API requests: send httpOnly session cookies with fetch.
 */
function enhanceApiRequestInit(input, init) {
  const href = getRequestUrl(input);
  if (href === "" || !isApiRequest(href, API_URL)) {
    return init;
  }
  const next = init && typeof init === "object" ? { ...init } : {};
  next.credentials = next.credentials ?? "include";
  return next;
}

const originalFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const nextInit = enhanceApiRequestInit(input, init);
  try {
    const response = await originalFetch(input, nextInit);

    if (response.status === 401) {
      const requestHref = getRequestUrl(input);
      if (requestHref === "" || !isApiRequest(requestHref, API_URL)) {
        return response;
      }

      const cleanPath = getCleanPath();
      const isPublic = isPublicPath(cleanPath);
      const isLoginPath = cleanPath === "/login";

      if (!isPublic && !isLoginPath) {
        clearSessionKeys({ preserveCart: true });
        originalFetch(`${API_URL}/api/debug-log`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            msg: "Redirecting to login due to 401",
            path: cleanPath,
            endpoint: requestHref,
          }),
        }).catch(() => {});
        const returnPath = `${window.location.pathname}${window.location.search}`;
        const loginTarget = `/login?redirect=${encodeURIComponent(returnPath)}`;
        window.location.replace(loginTarget);
      }
    }

    return response;
  } catch (error) {
    // Si es un error de red (como el bloqueo de CORS cuando el server está caído), lo propagamos
    // No redirigimos al login inmediatamente para no molestar si es algo temporal del servidor
    throw error;
  }
};


function BrowserTitleReset() {
  const location = useLocation();

  React.useEffect(() => {
    document.title = APP_BRAND_NAME;
  }, [location.pathname, location.search]);

  return null;
}

function ScrollToTop() {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  React.useEffect(() => {
    purgeLegacyAuthToken();
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <MensajesProvider>
      <BrowserTitleReset />
      <ScrollToTop />
      <BrowserSessionGate />
      <SessionInactivityWatcher />
      <WhatsAppFloatButton />
      <Routes>
        {/* --- Público (HeaderPublico) --- */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<PaginaPublicitaria />} />
          <Route path="/cursos" element={<CoursesPage />} />
          <Route
            path="/curso/:id"
            element={
              <BasePresentacionCursos>
                <PaginaCursoPublico />
              </BasePresentacionCursos>
            }
          />
          <Route path="/pago" element={<Pago />} />              {/* antiguo (carrito local) */}
          <Route path="/pago/:pedidoId" element={<PagoRedirect />} />  {/* NUEVO flujo */}
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        </Route>

        {/* --- Login --- */}
        <Route path="/login" element={<Login />} />
        <Route path="/recuperar-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/setup-account" element={<SetupAccount />} />
        <Route path="/invitar-pago" element={<InvitePaymentLanding />} />

        {/*nuevo Registro*/}
          <Route path="/NewRegistro" element={<NewRegistro />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/privacidad" element={<Privacidad />} />

        {/* --- Usuario (SidebarUser + HeaderPrivado con sobre) --- */}
        <Route
          path="/usuario/*"
          element={
            <RoleRoute allowedRoles={ALUMNO_ROLES}>
              <StudentProvider>
                <StudentLayout />
              </StudentProvider>
            </RoleRoute>
          }
        >
          <Route index element={<Campus />} />
          <Route path="campus" element={<Campus />} />
          <Route path="comunicados" element={<StudentComunicados />} />
          <Route path="campus/mensajeria-interna" element={<Navigate to="/usuario/mensajeria-interna" replace />} />
          <Route path="resumenes" element={<Resumenes />} />
          <Route path="test-temas" element={<TestTemas />} />
          <Route path="test-examen" element={<TestExamen />} />
          <Route path="rendimiento" element={<Rendimiento />} />
          <Route path="informacion" element={<Informacion />} />
          <Route path="mensajeria-interna" element={
            <MessagingGuard>
              <MensajeriaInterna />
            </MessagingGuard>
          } />
          <Route path="perfil" element={<Perfil />} />
          <Route path="curso/:id" element={<CursoUsuarioRedirect />} />
          <Route path=":tabSlug" element={<StudentTabRedirect />} />
        </Route>

        {/* --- Admin/Privado (PrivateLayoutAdmin + HeaderPrivado con sobre) --- */}
        <Route element={
          <PrivateRoute>
            <StudentProvider>
              <PrivateLayoutAdmin />
            </StudentProvider>
          </PrivateRoute>
        }>
          {/* Gestión por rol */}
          <Route path="/gestion-director" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES}><GestionDirector/></RoleRoute>} />
          <Route path="/gestion-vicedirector" element={<Navigate to="/gestion-director" replace />} />
          <Route path="/gestion-seccion" element={<Navigate to="/gestion-director" replace />} />
          <Route path="/gestion-departamento" element={<Navigate to="/gestion-director" replace />} />
          <Route path="/gestion-equipo" element={<Navigate to="/gestion-director" replace />} />
          <Route path="/tecnico" element={<Navigate to="/gestion-director" replace />} />
          <Route path="/gestion-instructor" element={<RoleRoute allowedRoles={["instructor"]}><GestionInstructor/></RoleRoute>} />
          <Route path="/director/progreso-alumnos" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES}><ProgresoAlumnos/></RoleRoute>} />
          <Route path="/instructor/progreso-alumnos" element={<RoleRoute allowedRoles={ROLES_PROGRESO_ALUMNOS}><ProgresoAlumnos/></RoleRoute>} />
          <Route path="/admin/editar-curso/:id" element={<RoleRoute allowedRoles={ROLES_CURSO_EDICION} requiredPermissions={["puedeModificar"]}><EditarCurso/></RoleRoute>} />
          <Route path="/admin/configurar-tests" element={<RoleRoute allowedRoles={ROLES_CURSO_EDICION} requiredPermissions={["puedeModificar"]}><ConfiguracionTests/></RoleRoute>} />
          <Route path="/admin/curso/:id/tests-config" element={<RoleRoute allowedRoles={ROLES_CURSO_EDICION} requiredPermissions={["puedeModificar"]}><ConfiguracionTests/></RoleRoute>} />

          {/* Director/Admin */}
          <Route path="/centro-control-director" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES} requiredPermissions={["puedeVerRegistros", "puedeBorrarUsuarios", "puedeBloquearUsuarios", "puedeCambiarRoles"]}><CentroControlDirector/></RoleRoute>} />
          <Route path="/GestionCursos" element={<RoleRoute allowedRoles={ROLES_CURSO_EDICION} requiredPermissions={["puedeModificar"]}><GestionCursos/></RoleRoute>} />
          <Route path="/GestionAnuncios" element={<RoleRoute allowedRoles={ROLES_GESTION_ANUNCIOS}><GestionAnuncios/></RoleRoute>} />
          <Route path="/Registro" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES} requiredPermissions={["puedeVerRegistros"]}><Registro/></RoleRoute>} />
          <Route path="/Perfil" element={<RoleRoute allowedRoles={STAFF_ROLES}><Perfil/></RoleRoute>} />
          <Route path="/registro-actividades" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES} requiredPermissions={["puedeVerRegistros"]}><ActivityLogQuery /></RoleRoute>} />

          <Route path="/centro-control-vicedirector" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/centro-control-JefeSeccion" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/centro-control-jefeSeccion" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/centro-control-JefeDepartamento" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/centro-control-jefeDepartamento" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/centro-control-JefeEquipo" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/centro-control-Tecnico" element={<Navigate to="/centro-control-director" replace />} />
          <Route path="/Autorizaciones" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES}><Autorizaciones/></RoleRoute>} />
          <Route path="/AutorizacionesJefes" element={<Navigate to="/Autorizaciones" replace />} />

          {/* Páginas/acciones */}
          <Route path="/Registrousuario" element={<RoleRoute allowedRoles={ROLES_REGISTRO_ALUMNOS} requiredPermissions={["puedeCrearUsuarios"]}><Registrousuario/></RoleRoute>} />
          <Route path="/GestorUsuarios/:id" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES} requiredPermissions={["puedeVerRegistros", "puedeBorrarUsuarios", "puedeBloquearUsuarios", "puedeCambiarRoles"]}><GestorUsuarios/></RoleRoute>} />
          <Route path="/CrearCurso" element={<RoleRoute allowedRoles={ROLES_CREAR_CURSO} requiredPermissions={["puedePublicar"]}><CrearCurso/></RoleRoute>} />
          <Route path="/admin/vista-cursos/:id" element={<RoleRoute allowedRoles={ROLES_CURSO_EDICION} requiredPermissions={["puedeModificar"]}><VistaCursosAdmin/></RoleRoute>} />
          <Route path="/curso-detalle/:id" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES}><CursoDetalle/></RoleRoute>} />

          {/*Configuracion */}
          <Route path="/Configuracion" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES}><Configuracion /></RoleRoute>} />
          <Route path="/configuracion-email" element={<RoleRoute allowedRoles={PLATFORM_DIRECTOR_ROLES}><EmailBienvenida /></RoleRoute>} />


          {/* Mensajería corporativa (admin) */}
          <Route path="/mensajeria-interna" element={<RoleRoute allowedRoles={STAFF_ROLES}><MensajeriaInterna/></RoleRoute>} />
        </Route>

        <Route path="/recurso-no-disponible" element={<RecursoNoDisponible />} />

        {/* 404 opcional */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </MensajesProvider>
    </Router>
  );
}

export default App;
